/**
 * Onde o balão do guia fica em volta do elemento que ele aponta. No protótipo as posições eram
 * fixas; no painel elas seguem o elemento e trocam de lado quando não há espaço (ajuste 8 da
 * revisão de 28/10/2026).
 */
export type Lado = 'esq' | 'dir' | 'cima' | 'baixo';

export interface Caixa {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Posicao {
  x: number;
  y: number;
  lado: Lado;
  /** Centro da setinha, dentro do balão. */
  setaX: number;
  setaY: number;
}

const OPOSTO: Record<Lado, Lado> = { esq: 'dir', dir: 'esq', cima: 'baixo', baixo: 'cima' };
const ORDEM: Lado[] = ['esq', 'dir', 'baixo', 'cima'];

function limitar(valor: number, min: number, max: number): number {
  return max < min ? min : Math.min(Math.max(valor, min), max);
}

function cabe(
  lado: Lado,
  alvo: Caixa,
  balao: { w: number; h: number },
  tela: { w: number; h: number },
  gap: number,
  margem: number,
): boolean {
  switch (lado) {
    case 'esq':
      return alvo.x - gap - balao.w >= margem;
    case 'dir':
      return alvo.x + alvo.w + gap + balao.w <= tela.w - margem;
    case 'cima':
      return alvo.y - gap - balao.h >= margem;
    case 'baixo':
      return alvo.y + alvo.h + gap + balao.h <= tela.h - margem;
  }
}

/** Posição do balão: o lado pedido, ou o oposto, ou o primeiro que couber. */
export function posicionar(
  alvo: Caixa,
  preferido: Lado,
  balao: { w: number; h: number },
  tela: { w: number; h: number },
  gap = 14,
  margem = 12,
): Posicao {
  const tentativas = [
    preferido,
    OPOSTO[preferido],
    ...ORDEM.filter((l) => l !== preferido && l !== OPOSTO[preferido]),
  ];
  const lado = tentativas.find((l) => cabe(l, alvo, balao, tela, gap, margem)) ?? preferido;
  const cx = alvo.x + alvo.w / 2;
  const cy = alvo.y + alvo.h / 2;
  let x: number;
  let y: number;
  if (lado === 'esq' || lado === 'dir') {
    x = lado === 'esq' ? alvo.x - gap - balao.w : alvo.x + alvo.w + gap;
    y = limitar(cy - Math.min(balao.h / 2, 40), margem, tela.h - margem - balao.h);
  } else {
    y = lado === 'cima' ? alvo.y - gap - balao.h : alvo.y + alvo.h + gap;
    x = limitar(cx - balao.w / 2, margem, tela.w - margem - balao.w);
  }
  x = limitar(x, margem, tela.w - margem - balao.w);
  y = limitar(y, margem, tela.h - margem - balao.h);
  const seta =
    lado === 'esq'
      ? { setaX: balao.w, setaY: limitar(cy - y, 18, balao.h - 18) }
      : lado === 'dir'
        ? { setaX: 0, setaY: limitar(cy - y, 18, balao.h - 18) }
        : lado === 'cima'
          ? { setaX: limitar(cx - x, 18, balao.w - 18), setaY: balao.h }
          : { setaX: limitar(cx - x, 18, balao.w - 18), setaY: 0 };
  return { x, y, lado, ...seta };
}

/** O elemento do passo, se estiver na tela (montado, com tamanho e sem `hidden` acima). */
export function acharAncora(seletor: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const lista = document.querySelectorAll<HTMLElement>(seletor);
  for (const el of lista) {
    if (el.closest('[hidden], [aria-hidden="true"]')) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}
